import { Observable } from '@logz-pkg/observable';
import type { ChatMessage, ChatStatus } from '@logz-pkg/models';
import type { IPriorConversation } from './prior-conversation.util';

class OrionChatStateService {
  readonly isDrawerOpen = new Observable<boolean>(false);
  readonly messages = new Observable<ChatMessage[]>([]);
  readonly sessionId = new Observable<string | undefined>(undefined);
  readonly title = new Observable<string | undefined>(undefined);
  readonly status = new Observable<ChatStatus>('idle');
  readonly disabledContextKeys = new Observable<ReadonlySet<string>>(new Set());
  readonly priorConversation = new Observable<IPriorConversation | undefined>(undefined);

  toggleDrawer = (isOpen: boolean): void => {
    this.isDrawerOpen.set(isOpen);
  };

  setContextKeyEnabled = (key: string, isEnabled: boolean): void => {
    const current = this.disabledContextKeys.get() ?? new Set<string>();

    if (isEnabled === !current.has(key)) return;

    const next = new Set(current);

    if (isEnabled) next.delete(key);
    else next.add(key);

    this.disabledContextKeys.set(next);
  };

  setContextKeysEnabled = (keys: readonly string[], isEnabled: boolean): void => {
    const current = this.disabledContextKeys.get() ?? new Set<string>();
    const next = new Set(current);

    keys.forEach(key => (isEnabled ? next.delete(key) : next.add(key)));

    if (next.size === current.size) return;

    this.disabledContextKeys.set(next);
  };

  /** Drop opt-outs for keys no longer on the page, so a row that returns comes back default-ON. */
  pruneToPresent = (presentKeys: readonly string[]): void => {
    const current = this.disabledContextKeys.get() ?? new Set<string>();

    if (current.size === 0) return;

    const present = new Set(presentKeys);
    const next = new Set([...current].filter(key => present.has(key)));

    if (next.size === current.size) return;

    this.disabledContextKeys.set(next);
  };

  attachPriorConversation = (priorConversation: IPriorConversation): void => {
    this.priorConversation.set(priorConversation);
  };

  startNewConversation = (sessionId: string): void => {
    this.sessionId.set(sessionId);
    this.messages.set([]);
    this.title.set(undefined);
    this.priorConversation.set(undefined);
    this.status.set('idle');
  };

  clear = (): void => {
    this.isDrawerOpen.set(false);
    this.messages.set([]);
    this.sessionId.set(undefined);
    this.title.set(undefined);
    this.priorConversation.set(undefined);
    this.status.set('idle');
    this.disabledContextKeys.set(new Set());
  };
}

export const orionChatStateService = new OrionChatStateService();
